'use server'

// Prim Hakediş — server action'ları. Liste verisi upgrade_bonus_items RPC'sinden KULLANICI oturumuyla gelir
// (RLS: owner/admin tüm operatörleri, diğerleri yalnız kendini görür). Dönem kapanışı ve "ödendi" işareti
// service_role ile, yetki kontrolünden sonra yazılır. [[opop-erpanel-prim-sistemi]]
//
// DİKKAT: 'use server' dosyası yalnız async fonksiyon export eder (sabitler lib/ modüllerinde).

import { createClient, createServiceClient } from '@repo/db/server'
import { getBonusAccess, requireBonusManager } from '@/lib/upgrade-bonus-access'
import { displayNameOf } from '@/lib/cargo-problem-operators'
import { createAdminClient } from '@/lib/supabase-admin'
import {
  istanbulMonthKey,
  shortUserLabel,
  summarizeUpgrades,
  toPayout,
  toUpgradeItem,
  type BonusSummary,
  type Payout,
  type PayoutRow,
  type UpgradeItem,
  type UpgradeItemRow,
} from '@/lib/upgrade-bonus'

/* eslint-disable @typescript-eslint/no-explicit-any */

/** Açık (döneme bağlanmamış) kayıtlar için geriye bakış. */
const OPEN_LOOKBACK_DAYS = 180
/** Kapanmış dönemlerin listelendiği ay sayısı. */
const SETTLED_MONTHS = 12

export type BonusOperatorInfo = {
  userId: string
  label: string
  /** Kişiye özel oran (NULL → varsayılan). */
  rate: number | null
  active: boolean
}

export type BonusWorkspace = {
  enabled: boolean
  isManager: boolean
  me: string
  defaultRate: number
  operators: BonusOperatorInfo[]
  items: UpgradeItem[]
  payouts: Payout[]
  generatedAt: string
}

export type BonusWidgetData = {
  summary: BonusSummary
  lastPayout: Payout | null
  rate: number
}

function monthsAgoKey(months: number, now = new Date()): string {
  const [y, m] = istanbulMonthKey(now).split('-').map(Number)
  const d = new Date(Date.UTC(y, m - 1 - months, 1))
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-01`
}

async function loadItems(tenantId: string, sinceDays: number, periodFrom: string): Promise<UpgradeItem[]> {
  const supabase = (await createClient()) as any
  const { data, error } = await supabase.rpc('upgrade_bonus_items', {
    p_tenant_id: tenantId,
    p_since: new Date(Date.now() - sinceDays * 86_400_000).toISOString(),
    p_period_from: periodFrom,
  })
  if (error) throw new Error(`Yükseltmeler alınamadı: ${error.message}`)
  return ((data ?? []) as UpgradeItemRow[]).map((r) => toUpgradeItem(r)).filter((x): x is UpgradeItem => !!x)
}

const PAYOUT_COLUMNS = 'id, user_id, user_label, period, upgrade_count, increase_total, bonus_total, computed_at, paid, paid_at, paid_by_label'

/** Kullanıcı etiketleri: ad (metadata) varsa ad, yoksa e-postanın @ öncesi. */
async function userLabels(userIds: string[]): Promise<Map<string, string>> {
  const admin = createAdminClient()
  const out = new Map<string, string>()
  await Promise.all(
    [...new Set(userIds)].map(async (id) => {
      const user = await admin.auth.admin.getUserById(id).then((r) => r.data.user).catch(() => null)
      if (user) out.set(id, shortUserLabel(displayNameOf(user)))
    }),
  )
  return out
}

/** Hakediş ekranı verisi. Açılışta (sistem açıksa) bekleyen dönem kapanışını da çalıştırır. */
export async function getBonusWorkspace(): Promise<BonusWorkspace> {
  const access = await getBonusAccess()
  const base: BonusWorkspace = {
    enabled: access.enabled,
    isManager: access.isManager,
    me: access.userId,
    defaultRate: access.defaultRate,
    operators: [],
    items: [],
    payouts: [],
    generatedAt: new Date().toISOString(),
  }

  if (access.enabled) {
    try {
      await (createServiceClient() as any).rpc('close_upgrade_bonus_periods')
    } catch {
      // kapanış saatlik cron'da da çalışır
    }
  }

  const supabase = (await createClient()) as any
  const [items, payoutsRes] = await Promise.all([
    loadItems(access.tenantId, OPEN_LOOKBACK_DAYS, monthsAgoKey(SETTLED_MONTHS)),
    supabase.from('upgrade_bonus_payouts').select(PAYOUT_COLUMNS).eq('tenant_id', access.tenantId).order('period', { ascending: false }).limit(1000),
  ])
  const payouts = ((payoutsRes.data ?? []) as PayoutRow[]).map(toPayout)

  let operators: BonusOperatorInfo[]
  if (access.isManager) {
    const { data: opRows } = await (createServiceClient() as any)
      .from('upgrade_bonus_operators')
      .select('user_id, active, rate')
      .eq('tenant_id', access.tenantId)
    const rows = (opRows ?? []) as { user_id: string; active: boolean; rate: number | string | null }[]
    const ids = new Set<string>(rows.map((r) => r.user_id))
    for (const it of items) if (it.userId) ids.add(it.userId)
    for (const p of payouts) if (p.userId) ids.add(p.userId)
    const labels = await userLabels([...ids])
    const byId = new Map(rows.map((r) => [r.user_id, r]))
    operators = [...ids]
      .map((id) => {
        const r = byId.get(id)
        return { userId: id, label: labels.get(id) ?? '—', rate: r?.rate == null ? null : Number(r.rate), active: !!r?.active }
      })
      .sort((a, b) => Number(b.active) - Number(a.active) || a.label.localeCompare(b.label, 'tr'))
  } else {
    operators = [{ userId: access.userId, label: shortUserLabel(access.email), rate: access.ownRate, active: access.isOperator }]
  }

  // Görünen adlar operatör listesiyle aynı olsun (kayıttaki e-posta yerine).
  const labelOf = new Map(operators.map((o) => [o.userId, o.label]))
  for (const it of items) if (it.userId && labelOf.has(it.userId)) it.userLabel = labelOf.get(it.userId)!
  for (const p of payouts) if (p.userId && labelOf.has(p.userId)) p.userLabel = labelOf.get(p.userId)!

  // RLS zaten süzer; yönetici olmayan için ikinci emniyet.
  const own = (uid: string | null) => access.isManager || uid === access.userId
  return { ...base, operators, items: items.filter((i) => own(i.userId)), payouts: payouts.filter((p) => own(p.userId)) }
}

/** Sol alttaki widget: yalnız prim alan aktif operatöre, yalnız kendi verisi. */
export async function getMyBonusWidget(): Promise<BonusWidgetData | null> {
  const access = await getBonusAccess()
  if (!access.enabled || !access.isOperator) return null
  const supabase = (await createClient()) as any
  const [items, { data: payoutRows }] = await Promise.all([
    loadItems(access.tenantId, 120, istanbulMonthKey(new Date())),
    supabase
      .from('upgrade_bonus_payouts')
      .select(PAYOUT_COLUMNS)
      .eq('tenant_id', access.tenantId)
      .eq('user_id', access.userId)
      .order('period', { ascending: false })
      .limit(1),
  ])
  const mine = items.filter((i) => i.userId === access.userId)
  return {
    summary: summarizeUpgrades(mine),
    lastPayout: payoutRows?.[0] ? toPayout(payoutRows[0] as PayoutRow) : null,
    rate: access.ownRate ?? access.defaultRate,
  }
}

/** Hakedişi ödendi / ödenmedi işaretler — yalnız owner/admin. */
export async function setBonusPayoutPaid(payoutId: string, paid: boolean): Promise<Payout> {
  const access = await requireBonusManager()
  if (typeof payoutId !== 'string' || !payoutId) throw new Error('Geçersiz hakediş')
  const service = createServiceClient() as any
  const { data, error } = await service
    .from('upgrade_bonus_payouts')
    .update({
      paid: !!paid,
      paid_at: paid ? new Date().toISOString() : null,
      paid_by: paid ? access.userId : null,
      paid_by_label: paid ? access.email : null,
    })
    .eq('id', payoutId)
    .eq('tenant_id', access.tenantId)
    .select(PAYOUT_COLUMNS)
    .maybeSingle()
  if (error) throw new Error(`Kaydedilemedi: ${error.message}`)
  if (!data) throw new Error('Hakediş bulunamadı')
  const payout = toPayout(data as PayoutRow)

  try {
    await service.from('audit_logs').insert({
      tenant_id: access.tenantId,
      actor_type: access.role === 'owner' ? 'owner' : 'admin',
      actor_id: access.userId,
      actor_name: access.email,
      action: paid ? 'upgrade_bonus.payout_paid' : 'upgrade_bonus.payout_unpaid',
      resource_type: 'upgrade_bonus_payout',
      resource_id: payout.id,
      resource_label: `${payout.periodLabel} · ${payout.userLabel} · ${payout.bonusTotal.toFixed(2)} TL`,
      metadata: { period: payout.period, user_id: payout.userId, bonus_total: payout.bonusTotal, paid: !!paid },
    })
  } catch {
    // best-effort
  }
  return payout
}
